import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import type { TierId } from "@/lib/tiers";

export interface MonthData {
  total_sales: number;
  total_sales_after_shipping: number;
  shipping_fees: number;
  commission_rate: number;
  commission_amount: number;
  orders_count: number;
}

/** One influencer with profile + per-month performance merged. */
export interface InfluencerRecord {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  social_media: string | null;
  avatar_url: string | null;
  coupon: string;
  role: string | null;
  tier_id: string | null;
  months: Record<string, MonthData>;
  total_withdrawn_pending: number;
  total_withdrawn_completed: number;
}

/** Aggregated totals for one calendar month across all influencers. */
export interface MonthlyTotals {
  month: string; // YYYY-MM
  sales: number;
  commissions: number;
  activeCount: number;
}

export interface InfluencersStats {
  totalInfluencers: number;
  activeInfluencers: number;
  totalSalesThisMonth: number;
  totalCommissions: number;
  pendingWithdrawalsAmount: number | null;
}

interface AnalyticsInfluencer {
  user_id: string;
  name: string | null;
  email: string | null;
  role: string | null;
  coupon: string;
  months: Record<string, MonthData>;
  total_withdrawn_pending: number;
  total_withdrawn_completed: number;
}

interface ProfileRow {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  social_media: string | null;
  instagram_avatar_url: string | null;
  coupon: string | null;
  role: string | null;
  tier_id: string | null;
}

export function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function currentMonthKey(): string {
  return monthKey(new Date());
}

export function previousMonthKey(): string {
  const now = new Date();
  return monthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1));
}

/** Active = sold something in the current or previous month. */
export function isActive(record: InfluencerRecord): boolean {
  const cur = record.months[currentMonthKey()];
  const prev = record.months[previousMonthKey()];
  return (cur?.total_sales || 0) > 0 || (prev?.total_sales || 0) > 0;
}

/**
 * Single data source for the Influencers dashboard: merges registered
 * influencer profiles (incl. tier) with the analytics API's per-month
 * performance data and the admin withdrawal stats.
 */
export function useInfluencersData() {
  const [rows, setRows] = useState<InfluencerRecord[]>([]);
  const [months, setMonths] = useState<string[]>([]);
  const [pendingWithdrawalsAmount, setPendingWithdrawalsAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [analyticsRes, profilesRes, statsRes] = await Promise.all([
        apiGet("/api/commission-balances/analytics"),
        supabase
          .from("influencers")
          .select("id, name, email, phone, social_media, instagram_avatar_url, coupon, role, tier_id"),
        apiGet("/api/withdrawals/admin-stats"),
      ]);

      if (!analyticsRes.ok) throw new Error("Erro ao carregar analytics");
      const analytics: { months: string[]; influencers: AnalyticsInfluencer[] } =
        await analyticsRes.json();

      const profiles: ProfileRow[] = profilesRes.error ? [] : (profilesRes.data as ProfileRow[]);

      const analyticsByUser = new Map(analytics.influencers.map((i) => [i.user_id, i]));
      const merged: InfluencerRecord[] = [];
      const seen = new Set<string>();

      for (const p of profiles) {
        const coupon = (p.coupon || "").toUpperCase().trim();
        if (coupon === "SILVA") continue; // internal account
        const a = analyticsByUser.get(p.id);
        seen.add(p.id);
        merged.push({
          id: p.id,
          name: p.name,
          email: p.email,
          phone: p.phone,
          social_media: p.social_media,
          avatar_url: p.instagram_avatar_url,
          coupon,
          role: p.role,
          tier_id: p.tier_id,
          months: a?.months || {},
          total_withdrawn_pending: a?.total_withdrawn_pending || 0,
          total_withdrawn_completed: a?.total_withdrawn_completed || 0,
        });
      }

      // Analytics rows without a registered profile (e.g. deleted account with
      // historical sales) still show up so the money stays visible.
      for (const a of analytics.influencers) {
        if (seen.has(a.user_id)) continue;
        if ((a.coupon || "").toUpperCase() === "SILVA") continue;
        merged.push({
          id: a.user_id,
          name: a.name,
          email: a.email,
          phone: null,
          social_media: null,
          avatar_url: null,
          coupon: a.coupon,
          role: a.role,
          tier_id: null,
          months: a.months,
          total_withdrawn_pending: a.total_withdrawn_pending,
          total_withdrawn_completed: a.total_withdrawn_completed,
        });
      }

      setRows(merged);
      setMonths(analytics.months);

      if (statsRes.ok) {
        const stats = await statsRes.json();
        setPendingWithdrawalsAmount(Number(stats.pending_requests || 0));
      }
    } catch (err) {
      console.error("Influencers data load error:", err);
      setError("Erro ao carregar dados de influenciadoras.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Optimistically apply a tier change already persisted by the selector. */
  const setTier = useCallback((userId: string, tierId: TierId | null) => {
    setRows((prev) => prev.map((r) => (r.id === userId ? { ...r, tier_id: tierId } : r)));
  }, []);

  /** Apply an avatar URL already persisted by the import endpoint. */
  const setAvatar = useCallback((userId: string, avatarUrl: string) => {
    setRows((prev) => prev.map((r) => (r.id === userId ? { ...r, avatar_url: avatarUrl } : r)));
  }, []);

  /** Per-month aggregates over the known months, sorted ascending. */
  const monthlyTotals: MonthlyTotals[] = useMemo(() => {
    const byMonth = new Map<string, MonthlyTotals>();
    for (const m of months) byMonth.set(m, { month: m, sales: 0, commissions: 0, activeCount: 0 });
    for (const r of rows) {
      for (const [m, data] of Object.entries(r.months)) {
        let entry = byMonth.get(m);
        if (!entry) {
          entry = { month: m, sales: 0, commissions: 0, activeCount: 0 };
          byMonth.set(m, entry);
        }
        entry.sales += data.total_sales || 0;
        entry.commissions += data.commission_amount || 0;
        if ((data.total_sales || 0) > 0) entry.activeCount++;
      }
    }
    return Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
  }, [rows, months]);

  const stats: InfluencersStats = useMemo(() => {
    const cur = currentMonthKey();
    let totalSalesThisMonth = 0;
    let totalCommissions = 0;
    let activeInfluencers = 0;
    for (const r of rows) {
      totalSalesThisMonth += r.months[cur]?.total_sales || 0;
      for (const m of Object.values(r.months)) totalCommissions += m.commission_amount || 0;
      if (isActive(r)) activeInfluencers++;
    }
    return {
      totalInfluencers: rows.length,
      activeInfluencers,
      totalSalesThisMonth,
      totalCommissions,
      pendingWithdrawalsAmount,
    };
  }, [rows, pendingWithdrawalsAmount]);

  return { rows, months, monthlyTotals, stats, loading, error, reload: load, setTier, setAvatar };
}
